import { type ReactNode, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuthStore } from "@/stores/useAuthStore";

type Props = { children: ReactNode };

/**
 * Reads :tenantSlug from the URL and cross-checks it against the JWT's
 * tenant_slug claim. On mismatch, clears the session and redirects to /login.
 *
 * Separate from RequireAuth — this component assumes RequireAuth has already
 * confirmed there IS a session; it only validates WHICH tenant the session is for.
 */
export function TenantContextProvider({ children }: Props): JSX.Element {
  const { tenantSlug: urlSlug } = useParams<{ tenantSlug: string }>();
  const navigate = useNavigate();
  const status = useAuthStore((state) => state.status);
  const jwtSlug = useAuthStore((state) => state.tenantSlug);
  const reset = useAuthStore((state) => state.reset);

  useEffect(() => {
    // Only validate when we have an authenticated session AND the URL has a slug.
    if (status !== "authenticated" || !urlSlug) return;

    if (jwtSlug && jwtSlug !== urlSlug) {
      // Slug mismatch: the user is trying to access a different tenant's workspace.
      reset();
      navigate("/login", { replace: true });
    }
  }, [status, urlSlug, jwtSlug, reset, navigate]);

  return <>{children}</>;
}
